import { authedJson } from './client';
import type {
  ExerciseHistory,
  ProgramState,
  ProgramSummary,
  SessionExercise,
  SessionSummary,
  WorkoutSession,
} from '../../../shared/types';

/** Auth plumbing every call needs, sourced from `useAuth()`. */
export interface Authed {
  getToken: () => string | null;
  refresh: () => Promise<boolean>;
}

const json = <T,>(path: string, options: RequestInit, a: Authed) =>
  authedJson<T>(path, options, a.getToken, a.refresh);

export const gymApi = {
  listPrograms: (a: Authed) => json<ProgramSummary[]>('/programs', { method: 'GET' }, a),

  getProgramState: (a: Authed) => json<ProgramState>('/me/program', { method: 'GET' }, a),

  /**
   * Start, switch to, or continue a program. Re-selecting the current program
   * preserves progress unless `restart` is true; switching to a different one
   * resets to day 1.
   */
  startProgram: (programId: string, weakPoint: string | null, a: Authed, restart = false) =>
    json<ProgramState>('/me/program', {
      method: 'POST',
      body: JSON.stringify({ programId, weakPoint, restart }),
    }, a),

  setWeakPoint: (weakPoint: string, a: Authed) =>
    json<ProgramState>('/me/program/weak-point', {
      method: 'PUT',
      body: JSON.stringify({ weakPoint }),
    }, a),

  /** Acknowledge the current rest day and move to the next entry. */
  advanceDay: (a: Authed) => json<ProgramState>('/me/program/advance', { method: 'POST' }, a),

  startWorkout: (a: Authed) => json<WorkoutSession>('/workouts', { method: 'POST' }, a),

  getActiveWorkout: (a: Authed) =>
    json<{ active: boolean; session?: WorkoutSession }>('/workouts/active', { method: 'GET' }, a),

  saveWorkout: (sessionId: string, exercises: SessionExercise[], a: Authed) =>
    json<WorkoutSession>(`/workouts/${sessionId}`, {
      method: 'PUT',
      body: JSON.stringify({ exercises }),
    }, a),

  completeWorkout: (sessionId: string, a: Authed) =>
    json<{ session: WorkoutSession; program: ProgramState }>(
      `/workouts/${sessionId}/complete`, { method: 'POST' }, a),

  /** Reopens the most recently completed workout (for an accidental finish). */
  undoLastWorkout: (a: Authed) =>
    json<{ session: WorkoutSession; program: ProgramState }>(
      '/workouts/undo-last', { method: 'POST' }, a),

  abandonWorkout: (sessionId: string, a: Authed) =>
    json<void>(`/workouts/${sessionId}/abandon`, { method: 'POST' }, a),

  listWorkouts: (a: Authed) => json<SessionSummary[]>('/workouts', { method: 'GET' }, a),

  getWorkout: (sessionId: string, a: Authed) =>
    json<WorkoutSession>(`/workouts/${sessionId}`, { method: 'GET' }, a),

  getExerciseHistory: (name: string, a: Authed) =>
    json<ExerciseHistory>(`/exercises/${encodeURIComponent(name)}/history`, { method: 'GET' }, a),
};
